import { defineQuiz } from "./_schema";
import type { Answers, PersonaKey, Question } from "./_schema";
import { deriveQuizTrtSwitchProfile } from "./quiz-trt-switch-profile";

/**
 * TRT-SWITCH QUIZ -- cold-traffic destination for Ad 1 ("I quit TRT, started
 * Mars Men. Got leaner than I ever did on injections").
 *
 * Audience: men currently ON TRT, frustrated that their number is high on paper
 * but they're still soft, tired, and tethered to a needle. The quiz makes them
 * count what TRT left unfixed, then hands 1:1 into the locked T-suppression
 * verdict surface -- reframed as the user's own (endogenous) production, which
 * exogenous TRT suppresses. New `exogenous-suppression` Primary Root Cause
 * (forced on every on-TRT path; see quizzes/quiz-trt-switch-profile.ts).
 *
 * 8 single/multi screens + 3 interstitials (2 bridges + analyzing) = 11.
 * `trt-still-true` (S3) is the first multi-select question in any registered
 * quiz. Design spec: docs/specs/2026-06-08-quiz-trt-switch-quiz-design.md.
 *
 * Like ignite, this quiz has no GLP-1 screener, so `glp1-user` is not reachable
 * from the classifier; `skeptical-buyer` is likewise unreached (no onset fork).
 */

const QUESTION_ORDER = [
  "age",
  "concern",
  "trt-duration",
  "trt-still-true",
  "energy-3pm",
  "body-composition",
  "info-absolution",
  "stress-symptoms",
  "trt-forever",
  "info-mechanism",
  "info-analyzing",
] as const;

const questions: Question[] = [
  {
    id: "age",
    type: "single",
    prompt: "How old are you?",
    options: [
      { value: "50-plus", label: "50+ years old" },
      { value: "40-49", label: "40–49 years old" },
      { value: "30-39", label: "30–39 years old" },
      { value: "20-29", label: "20–29 years old" },
    ],
  },
  {
    id: "concern",
    type: "single",
    // POSITIVE / aspiration beat. Reused live quiz-v6 id (values verbatim,
    // deriver-contract) re-skinned to TRT language. Intro hero renders above.
    prompt: "When you started TRT, what were you chasing?",
    options: [
      { value: "energy", label: "My energy back" },
      { value: "sex-drive", label: "My sex drive back" },
      { value: "muscle", label: "Muscle and strength" },
      { value: "weight-loss", label: "To finally lose the belly" },
      { value: "focus", label: "Sharper focus and drive" },
    ],
  },
  {
    id: "trt-duration",
    type: "single",
    prompt: "How long have you been on TRT?",
    options: [
      { value: "2-plus-years", label: "2+ years" },
      { value: "1-2-years", label: "1–2 years" },
      { value: "under-year", label: "Under a year" },
      { value: "just-started", label: "Just started (under 3 months)" },
      { value: "not-on-trt", label: "I'm not on TRT yet" },
    ],
  },
  {
    id: "trt-still-true",
    type: "multi",
    // Tolerance check (NEGATIVE) -- the core agitation: TRT under-delivered.
    // First multi-select in the repo; answer_id joins selected values with '+'
    // (rule 22). Each check feeds severity via the wrapper deriver;
    // `still-belly` / `numbers-fine` are the estrogen-conversion tells.
    prompt: "Even on TRT, which of these are still true for you?",
    helper: "Select all that apply.",
    options: [
      { value: "still-belly", label: "I still carry belly fat" },
      { value: "energy-crash", label: "My energy still crashes" },
      { value: "low-drive", label: "My sex drive still isn't fully back" },
      { value: "mood", label: "Mood swings or irritability" },
      { value: "acne-oily", label: "Acne or oily skin" },
      { value: "shrinkage", label: "Shrinkage down there" },
      { value: "numbers-fine", label: "My numbers look “fine” but I don't feel it" },
    ],
  },
  {
    id: "energy-3pm",
    type: "single",
    // Live quiz-v6 verbatim, incl. emojis (kept here, unlike cortisol). Option
    // values byte-identical (deriver contract); pairs with stress-symptoms for
    // the cortisol-dominance Secondary score.
    prompt: "How's your energy at 3pm compared to when you wake up?",
    options: [
      { value: "drained", label: "Completely drained — need caffeine or a nap", emoji: "🪫" },
      { value: "afternoon-slump", label: "Noticeably lower — afternoon slump hits hard", emoji: "😴" },
      { value: "slightly-tired", label: "Slightly tired — still functional but slower", emoji: "😐" },
      { value: "same-energy", label: "Same energy — no afternoon crash", emoji: "⚡" },
    ],
  },
  {
    id: "body-composition",
    type: "single",
    // Live quiz-v6 verbatim values; deriver + classifier. whyWeAsk stays
    // deliberately mechanism-silent -- the aromatase / estrogen reveal is held
    // for Bridge 2, keeping the loop open.
    prompt: "How would you describe your body composition right now?",
    options: [
      { value: "lean-muscular", label: "Lean and muscular" },
      { value: "average", label: "Average build" },
      { value: "soft-some-muscle", label: "Soft with some muscle" },
      { value: "overweight", label: "Carrying real belly fat" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "On TRT, body composition changes how much of your testosterone stays usable. This shapes your profile — we'll explain what's driving it in a moment.",
    },
  },
  {
    id: "info-absolution",
    type: "info",
    // BRIDGE 1 -- absolution. Mechanism-silent (no estrogen / SHBG / cortisol
    // named yet -- that's Bridge 2). Lands after the tolerance check + symptoms,
    // before the personal anchors.
    heading: "It's not that you failed TRT.",
    body: [
      "You did the responsible thing. You got your levels checked, you started treatment, and on paper it worked — your number went up.",
      "But the number was never the whole problem. TRT floods your system with testosterone and hopes for the best. What happens to that testosterone next, it was never built to address.",
      "That's not on you. A few more questions and we'll show you exactly what's been missed.",
    ],
  },
  {
    id: "stress-symptoms",
    type: "single",
    // Live quiz-v6 verbatim (yes/no). `yes` fires cortisol-dominance +
    // chronic-stress scoring in the deriver.
    prompt: "Most days, do you feel wired-but-tired — stressed, but still running on empty?",
    options: [
      { value: "yes", label: "Yes — that's me" },
      { value: "no", label: "No, not really" },
    ],
  },
  {
    id: "trt-forever",
    type: "single",
    // NEGATIVE / commitment beat. Feeds a severity bump (worried / want-off) and
    // sets up Bridge 2's dependency reveal + the offer's emotional close.
    prompt: "When you picture being on TRT for the rest of your life, how does that sit with you?",
    options: [
      { value: "fine", label: "Fine — I've made peace with it" },
      { value: "uneasy-needles", label: "Uneasy about the needles" },
      { value: "worried-dependency", label: "Worried about the dependency" },
      { value: "want-off", label: "I want off — but I'm scared my T will crash" },
    ],
  },
  {
    id: "info-mechanism",
    type: "info",
    // BRIDGE 2 -- name the villain (three thieves + the exogenous shutdown).
    // Closing line is the commitment pivot; primes the natural-production
    // verdict + the analyzing beat. Source: Ad 1 mechanism stanza +
    // trt-nervous "three thieves", compressed. Product facts (if added) come
    // from docs/product-brief.md verbatim.
    heading: "TRT raised your number. It never touched the three thieves.",
    body: [
      "Even when your testosterone is high, three things quietly steal it back: an enzyme that converts it into estrogen, a protein (SHBG) that locks it up so you can't use it, and cortisol that tells your body to store fat instead. TRT addresses none of them.",
      "And there's a cost it doesn't mention: flood your body with testosterone from outside, and it stops making its own. That's the dependency — your own production goes dormant.",
      "Dormant isn't dead. Let's see how shut down yours has become — and what it takes to switch it back on.",
    ],
  },
  {
    id: "info-analyzing",
    type: "info",
    // Pays off Bridge 2's closing line ("how shut down yours has become /
    // switch it back on").
    heading: "Analyzing your natural production…",
    body: [
      "Counting what TRT left unfixed — and measuring how shut down your own production has become.",
    ],
    animation: "analyzing",
    autoAdvanceMs: 5000,
  },
];

export const quizTrtSwitchQuiz = defineQuiz({
  id: "quiz-trt-switch",
  slug: "quiz-trt-switch",
  meta: {
    // Doubles as the Shopify page-title default at publish.
    title: "Is TRT Really Working — or Just Flooding It?",
    introHeadline: "Your testosterone is high on paper. So why are you still STUCK?",
    introAccent: "stuck",
    introBody:
      "60 seconds. See what TRT floods past — and whether your body can still do it on its own.",
    testimonial: {
      // Quiz-benefit entry proof (replaces the product review that named the
      // brand): reveal-safe, switcher-flavored, no product or number. The real
      // greenlit dr-latt switch review now belongs on the result page, not here.
      // Operator to back the Verified tag with a real review before relying on it.
      quote:
        "Been on TRT a while and just assumed I’d be on it for life. Taking this quiz showed me how to actually think through my options. Really glad I did.",
      author: "Michael W., Verified Review",
    },
  },
  questions,

  branching(_answers: Answers, currentQuestionId: string): string | null {
    const idx = QUESTION_ORDER.indexOf(currentQuestionId as (typeof QUESTION_ORDER)[number]);
    if (idx < 0) return null;
    const next = QUESTION_ORDER[idx + 1];
    return next ?? null;
  },

  classifier(answers: Answers): PersonaKey {
    const dur = answers["trt-duration"];
    const onTrt = typeof dur === "string" && dur !== "not-on-trt";
    // The whole on-TRT audience is the canonical "Men Seeking TRT Alternatives"
    // archetype -- route them all to trt-alternative (dominant route).
    if (onTrt) return "trt-alternative";

    // Rare not-on-TRT completer: route organically (no forced cause downstream).
    const age = answers["age"];
    const body = answers["body-composition"];
    const concern = answers["concern"];
    if (body === "lean-muscular" && (concern === "muscle" || concern === "focus")) {
      return "performance";
    }
    if (age === "50-plus" || age === "40-49") return "aging-men";
    return "everyday-man";
  },

  deriveProfile: deriveQuizTrtSwitchProfile,
});
